import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Drawer, Table, Tag, Tooltip, Typography } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { LuInfo } from "react-icons/lu";
import CustomButton from "../../commonComponents/button";
import DebounceInput from "../../commonComponents/DebounceInput";
import CustomAvatar from "../../commonComponents/CustomAvatar";
import MediaLabel from "./MediaLabel";
import { DATE_TIME_FORMATS, formatTimeInSeconds } from "../../../utils/dateTimes";
import { convertUtcToLocal } from "../../../library/utils";
import { COMMON_PAGINATION } from "../../../library/constants";
import { GET_USER_MEDIA_ENGAGEMENT } from "../../../graphql/query";
import useLeadRedirection, { REDIRECTION_TYPES } from "../../../hooks/useLeadRedirection";
import {
  LINK_TRACKING_NOTICE,
  MEDIA_TYPE,
  MEDIA_TYPE_LABELS,
  MEDIA_DETAIL_STATUS_COLOR,
  MEDIA_DETAIL_STATUS_LABEL,
  MEDIA_TYPE_TO_API,
} from "../activityCenter/mediaTrackingHelpers";

const { Text } = Typography;

const TYPE_LABEL_MAP = {
  [MEDIA_TYPE.IMAGE]: "images",
  [MEDIA_TYPE.VIDEO]: "videos",
  [MEDIA_TYPE.DOC]: "documents",
};

const formatDate = value =>
  value ? convertUtcToLocal(value, DATE_TIME_FORMATS.MMM_D_YYYY_hh_mm_A) : "--";

// Wraps the shared dateTimes helper but returns "" (instead of "0s") for null/zero,
// so the row cell falls back to "--" in the table.
const formatWatchTime = totalSeconds => {
  const n = Number(totalSeconds);
  if (!n || n <= 0) return "";
  return formatTimeInSeconds(n);
};

// Pulls the filename out of a media URL (strips query/hash, decodes %20 etc.).
const getFileNameFromUrl = url => {
  if (!url || typeof url !== "string") return "";
  try {
    const pathname = url.split(/[?#]/)[0];
    const segment = pathname.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(segment);
  } catch {
    return "";
  }
};

const buildChildLabel = (mediaType, index, mediaUrl) =>
  getFileNameFromUrl(mediaUrl) ||
  `${MEDIA_TYPE_LABELS[mediaType]?.singular || "Item"} ${index + 1}`;

// Builds the "Yes" / "Yes (N times)" node for a child row's Downloaded cell.
const buildDownloadedNode = (isDownloaded, count) => {
  if (!isDownloaded) return "";
  const n = Number(count) || 0;
  return (
    <>
      Yes
      {n > 1 && <span className="!text-[#52C41A] !font-normal !text-xs"> ({n} times)</span>}
    </>
  );
};

// Returns the thumbnail URL for the row preview. If no thumbnail, MediaLabel
// renders the type-specific fallback icon.
const getPreviewUrl = item => item?.thumbnail || "";

// Transforms `leads[]` from the API into Antd Table rows with child rows from `items[]`.
const buildRows = (leads, mediaType) =>
  (Array.isArray(leads) ? leads : []).map(lead => {
    const items = Array.isArray(lead?.items) ? lead.items : [];
    const hasChildren = items.length > 1;
    // Items come back ordered latest-first, so items[0] is the most recent record.
    // Parent row mirrors that latest item for sentAt/method/status/openedAt/watchTime;
    // mediaCount and downloadedCount stay as lead-level rollups.
    const firstItem = items[0] || null;
    const downloadedCount = Number(lead?.downloadedCount) || 0;
    return {
      key: `lead-${lead.leadId}`,
      leadId: lead.leadId,
      leadName: lead.leadName,
      mediaCount: lead.totalMedia,
      thumbnail: getPreviewUrl(firstItem),
      sentAt: firstItem?.sentAt || null,
      method: firstItem?.method || null,
      link: firstItem?.link || null,
      status: firstItem?.status || null,
      openedAt: firstItem?.openedAt || null,
      watchTime: formatWatchTime(firstItem?.totalPlayDuration),
      downloaded: downloadedCount > 0 ? String(downloadedCount) : "",
      children: hasChildren
        ? items.map((item, idx) => ({
            key: `item-${item.mmsObjectId ?? `${lead.leadId}-${idx}`}`,
            label: buildChildLabel(mediaType, idx, item.mediaUrl),
            thumbnail: getPreviewUrl(item),
            sentAt: item.sentAt,
            method: item.method,
            link: item.link,
            status: item.status,
            openedAt: item.openedAt,
            watchTime: formatWatchTime(item.totalPlayDuration),
            downloaded: buildDownloadedNode(item.isDownloaded, item.downloadCount),
          }))
        : undefined,
    };
  });

const NOWRAP_CELL_PROPS = { style: { whiteSpace: "nowrap" } };
const cellNowrap = () => NOWRAP_CELL_PROPS;

const getColumns = (mediaType, redirectToLead) => {
  const isChild = record => !!record?.label;

  const baseColumns = [
    {
      title: "Lead name",
      width: 200,
      ellipsis: { showTitle: true },
      onCell: cellNowrap,
      render: (_, record) => {
        if (isChild(record)) return null;
        return (
          <span className="flex items-center gap-2 whitespace-nowrap">
            <CustomAvatar name={record.leadName} bgClass="bg-grey-700" />
            <span
              title="Open in activity center"
              className="text-twLinkColor !text-sm ellipsis cursor-pointer"
              onClick={() => record.leadId && redirectToLead?.(record.leadId)}
            >
              {record.leadName}
            </span>
          </span>
        );
      },
    },
    { title: "", width: 48 },
    {
      title: "Media",
      width: 200,
      onCell: cellNowrap,
      render: (_, record) => {
        if (isChild(record)) {
          return <MediaLabel type={mediaType} thumbnail={record.thumbnail} label={record.label} />;
        }
        return (
          <MediaLabel type={mediaType} thumbnail={record.thumbnail} count={record.mediaCount} />
        );
      },
    },
    {
      title: "Sent at",
      width: 200,
      onCell: cellNowrap,
      render: (_, record) => formatDate(record.sentAt),
    },
    {
      title: (
        <span className="inline-flex items-center gap-1">
          Method
          <Tooltip title={LINK_TRACKING_NOTICE} overlayStyle={{ maxWidth: 320 }}>
            <LuInfo className="text-black-45 cursor-help text-xs" />
          </Tooltip>
        </span>
      ),
      width: 110,
      onCell: cellNowrap,
      render: (_, record) => {
        if (!record.method) return <Text className="!text-sm">--</Text>;
        if (record.method === "LINK") {
          return (
            <CustomButton
              type="link"
              btnText="Link"
              icon={<LinkOutlined />}
              size="small"
              className="!p-0 !h-auto"
              disabled={!record.link}
              handleClick={() => {
                if (record.link) window.open(record.link, "_blank", "noopener,noreferrer");
              }}
            />
          );
        }
        return <Text className="!text-sm">MMS</Text>;
      },
    },
    {
      title: "Status",
      width: 110,
      onCell: cellNowrap,
      render: (_, record) => {
        const { status } = record;
        if (!status) return null;
        return (
          <Tag color={MEDIA_DETAIL_STATUS_COLOR[status]}>
            {MEDIA_DETAIL_STATUS_LABEL[status] || status}
          </Tag>
        );
      },
    },
  ];

  baseColumns.push({
    title: "Downloaded",
    width: 130,
    onCell: cellNowrap,
    render: (_, record) => (
      <Text className={`!text-sm ${record.downloaded ? "!text-[#52C41A]" : ""}`}>
        {record.downloaded || "--"}
      </Text>
    ),
  });

  baseColumns.push({
    title: "Opened at",
    width: 200,
    onCell: cellNowrap,
    render: (_, record) => formatDate(record.openedAt),
  });

  if (mediaType === MEDIA_TYPE.VIDEO) {
    baseColumns.push({
      title: "Watch time",
      width: 140,
      onCell: cellNowrap,
      render: (_, record) => {
        if (!record.watchTime) return <Text className="!text-sm">--</Text>;
        return <p className="text-black-88 text-sm whitespace-nowrap">{record.watchTime}</p>;
      },
    });
  }

  return baseColumns;
};

const MessageTypeDetailDrawer = ({
  open,
  onClose,
  mediaType,
  userId,
  userName,
  count,
  filterLabel,
  filterType,
  dateRange,
  opportunityTypeId,
}) => {
  const [params, setParams] = useState({ page: 1, pageSize: 10 });
  const [search, setSearch] = useState("");

  const { redirectToLead } = useLeadRedirection();

  const [fetchEngagement, { data, loading }] = useLazyQuery(GET_USER_MEDIA_ENGAGEMENT, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!open || !userId || !mediaType) return;
    fetchEngagement({
      variables: {
        userId: Number(userId),
        mediaType: MEDIA_TYPE_TO_API[mediaType],
        filterType,
        dateRange,
        opportunityTypeId: opportunityTypeId || undefined,
        page: params.page,
        pageSize: params.pageSize,
        search: search || undefined,
      },
    });
  }, [
    open,
    userId,
    mediaType,
    filterType,
    dateRange,
    opportunityTypeId,
    params.page,
    params.pageSize,
    search,
    fetchEngagement,
  ]);

  // Reset to page 1 each time the drawer is reopened or search changes
  useEffect(() => {
    if (open) {
      setParams({ page: 1, pageSize: 10 });
      setSearch("");
    }
  }, [open, userId, mediaType]);

  // When search changes, reset to first page
  useEffect(() => {
    setParams(prev => ({ ...prev, page: 1 }));
  }, [search]);

  const engagement = data?.getUserMediaEngagement;
  const rows = useMemo(() => buildRows(engagement?.leads, mediaType), [engagement, mediaType]);
  const total = engagement?.totalLeads || 0;
  const columns = useMemo(() => getColumns(mediaType, redirectToLead), [mediaType, redirectToLead]);
  const typeLabel = TYPE_LABEL_MAP[mediaType] || "items";

  return (
    <Drawer
      title={
        <div className="flex flex-col">
          <Text className="!text-black-88 !text-base !font-semibold">{userName}</Text>
          <Text className="!text-black-45 !font-normal !text-sm">
            {count} {typeLabel} sent in {filterLabel}
          </Text>
        </div>
      }
      width={1400}
      closable
      onClose={onClose}
      open={open}
      styles={{ body: { padding: "12px 16px" } }}
    >
      <DebounceInput
        placeholder="Search by lead name"
        value={search}
        onChange={setSearch}
        allowClear
        className="!mb-3"
        inputWidthClass="!w-72"
      />
      <Table
        bordered={false}
        loading={loading}
        dataSource={rows}
        columns={columns}
        size="small"
        tableLayout="fixed"
        scroll={{ x: "max-content", y: "calc(100vh - 240px)" }}
        expandable={{
          expandIconColumnIndex: 1,
        }}
        pagination={{
          current: params.page,
          pageSize: params.pageSize,
          total,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: COMMON_PAGINATION.PAGE_SIZE_OPTIONS,
          position: ["bottomLeft"],
          showTotal: () => `Showing ${rows.length} out of ${total} ${typeLabel}`,
          onChange: (page, pageSize) => setParams({ page, pageSize }),
        }}
      />
    </Drawer>
  );
};

export default MessageTypeDetailDrawer;
